import React, { useCallback, useEffect, useState } from 'react';
import { Popover } from '@headlessui/react';
import { getAdminAnalyticsApi } from 'src/services/api';
import { ADMIN_DATA_CHANGED_EVENT } from './AdminContext';
import {
  defaultData,
  timeRanges,
  cardConfigs,
  DashboardHeader,
  StatCard,
  StatusBar,
  ErrorBanner,
  DashboardSkeleton,
  SubscriptionChart,
  GrowthMetricsCard,
  InsightsCard,
  DashboardFooter,
} from './dashboard';
import type { AnalyticsData, TimeRange } from './dashboard';
import { getRangeFromTimeRange, parseLocalDate, calculateTrend } from './dashboard/utils';

const STORAGE_USER = 'flower_power_notes_user';

function getTrend(
  data: AnalyticsData,
  trendKey: string | null
): { value: number; positive: boolean } | null {
  if (!trendKey) return null;
  if (trendKey === 'activeUsersLast30Days') {
    const current = data.activeUsersInRange ?? data.activeUsersLast30Days ?? 0;
    const previous = data.activeUsersPrevious;
    if (previous === undefined) return null;
    return calculateTrend(current, previous);
  }
  if (trendKey === 'newSignups') {
    const current = data.newSignupsInRange ?? 0;
    const previous = data.newSignupsPrevious;
    if (previous === undefined) return null;
    return calculateTrend(current, previous);
  }
  return null;
}

export default function AdminDashboard() {
  const [data, setData] = useState<AnalyticsData>(defaultData);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [timeRange, setTimeRange] = useState<TimeRange>(timeRanges[2]);
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [customOpen, setCustomOpen] = useState(false);

  const load = useCallback(
    async (opts?: {
      isRefreshing?: boolean;
      overrideFrom?: string;
      overrideTo?: string;
      overrideValue?: string;
    }) => {
      const isRefreshing = opts?.isRefreshing ?? false;
      if (!isRefreshing) setLoading(true);
      setRefreshing(true);
      setError(null);

      try {
        const raw = localStorage.getItem(STORAGE_USER);
        const user = raw ? JSON.parse(raw) : {};
        const authToken = user?.AuthToken ?? '';
        const value = opts?.overrideValue ?? timeRange.value;
        const { from, to } =
          opts?.overrideFrom != null && opts?.overrideTo != null
            ? { from: opts.overrideFrom, to: opts.overrideTo }
            : getRangeFromTimeRange(value, customFrom || undefined, customTo || undefined);

        const result = await getAdminAnalyticsApi(authToken, { from, to });

        if (result.error) {
          setError(
            typeof result.error === 'string' ? result.error : 'Failed to load analytics data'
          );
          return;
        }

        const totalUsers = result.totalUsers ?? 0;
        const activeUsersLast30Days =
          result.activeUsersLast30Days ?? result.activeUsersInRange ?? 0;
        const engagementRate =
          totalUsers > 0 ? Math.round((activeUsersLast30Days / totalUsers) * 100) : 0;

        const newData: AnalyticsData = {
          totalUsers,
          onlineUsers: result.onlineUsers ?? 0,
          activeUsersLast30Days,
          totalContacts: result.totalContacts ?? 0,
          subscriptionActive: result.subscriptionActive ?? 0,
          subscriptionCanceled: result.subscriptionCanceled ?? 0,
          newUsersThisWeek: result.newUsersThisWeek ?? 0,
          newUsersThisMonth: result.newUsersThisMonth ?? 0,
          newSignupsInRange: result.newSignupsInRange ?? 0,
          revenue: 0,
          engagementRate,
          activeUsersInRange: result.activeUsersInRange,
          activeUsersPrevious: result.activeUsersPrevious,
          newSignupsPrevious: result.newSignupsPrevious,
        };

        setData(newData);
        setLastUpdated(new Date());
      } catch (err) {
        setError('An unexpected error occurred. Please try again.');
        console.error('Analytics load error:', err);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [timeRange.value, customFrom, customTo]
  );

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const handler = () => load({ isRefreshing: true });
    window.addEventListener(ADMIN_DATA_CHANGED_EVENT, handler);
    return () => window.removeEventListener(ADMIN_DATA_CHANGED_EVENT, handler);
  }, [load]);

  const applyPresetRange = (range: TimeRange) => {
    setTimeRange(range);
    if (range.value !== 'custom') {
      const { from, to } = getRangeFromTimeRange(range.value);
      load({ overrideFrom: from, overrideTo: to });
    } else {
      setCustomOpen(true);
    }
  };

  const applyCustomRange = () => {
    if (!customFrom || !customTo) return;
    const fromDate = parseLocalDate(customFrom);
    const toDate = parseLocalDate(customTo);
    if (!fromDate || !toDate || fromDate > toDate) return;
    const toEnd = new Date(
      toDate.getFullYear(),
      toDate.getMonth(),
      toDate.getDate(),
      23,
      59,
      59,
      999
    );
    setTimeRange({ label: 'Custom', value: 'custom' });
    setCustomOpen(false);
    load({ overrideFrom: fromDate.toISOString(), overrideTo: toEnd.toISOString() });
  };

  const exportData = () => {
    const csvContent = Object.entries(data)
      .map(([key, value]) => `${key},${value}`)
      .join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `analytics-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const activeTrend = getTrend(data, 'activeUsersLast30Days');
  const activeTrendValue = activeTrend?.value ?? 0;

  return (
    <div className="flex flex-col flex-1 bg-gradient-to-b from-gray-50 to-white w-full min-h-0 overflow-hidden">
      <DashboardHeader
        timeRange={timeRange}
        customFrom={customFrom}
        customTo={customTo}
        customOpen={customOpen}
        onPresetRange={applyPresetRange}
        onCustomFromChange={setCustomFrom}
        onCustomToChange={setCustomTo}
        onCustomOpenChange={setCustomOpen}
        onApplyCustom={applyCustomRange}
        onExport={exportData}
        onRefresh={() => load({ isRefreshing: true })}
        refreshing={refreshing}
      />

      <div className="flex flex-col flex-1 p-1.5 min-w-0 min-h-0 overflow-hidden">
        <StatusBar
          lastUpdated={lastUpdated}
          refreshing={refreshing}
          engagementRate={data.engagementRate ?? 0}
          newUsersThisWeek={data.newUsersThisWeek}
        />

        {error && (
          <ErrorBanner message={error} onRetry={() => load()} />
        )}

        {loading && !refreshing ? (
          <DashboardSkeleton />
        ) : (
          <div className="flex-1 content-stretch gap-1 grid grid-rows-[auto_1fr_auto] min-h-0">
            <div className="flex justify-end items-center gap-0.5 shrink-0">
              <Popover className="relative">
                <Popover.Button
                  type="button"
                  className="flex justify-center items-center gap-0.5 hover:bg-gray-200 px-1 py-0.5 rounded focus:outline-none focus:ring-2 focus:ring-gray-400 font-medium text-black text-xs"
                  aria-label="How stats are calculated"
                >
                  <span>Help</span>
                  <span className="flex justify-center items-center rounded-full w-4 h-4 font-bold">?</span>
                </Popover.Button>
                <Popover.Panel className="right-0 z-50 absolute bg-white shadow-lg mt-1 p-2 border border-gray-200 rounded-lg w-[260px] max-h-[280px] overflow-y-auto">
                  <p className="mb-1.5 font-semibold text-gray-900 text-xs">How stats are calculated</p>
                  <ul className="space-y-1 text-[11px] text-gray-700">
                    {cardConfigs.map((card) => (
                      <li key={card.label}>
                        <span className="font-medium text-gray-900">{card.label}:</span>{' '}
                        {card.description}
                      </li>
                    ))}
                  </ul>
                </Popover.Panel>
              </Popover>
            </div>
            <div className="gap-1 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-4 xl:grid-cols-4 min-h-0">
              {cardConfigs.map((card) => {
                const raw = data[card.valueKey];
                const value = typeof raw === 'number' ? raw : 0;
                const trend = getTrend(data, card.trendKey);
                const miniData = [value * 0.7, value * 0.9, value, value * 0.8, value * 0.6];
                return (
                  <StatCard
                    key={card.label}
                    config={card}
                    value={value}
                    trend={trend}
                    miniChartData={miniData}
                  />
                );
              })}
            </div>

            <div className="gap-1 grid grid-cols-1 lg:grid-cols-2 min-h-0">
              <SubscriptionChart data={data} />
              <GrowthMetricsCard data={data} timeRangeLabel={timeRange.label} />
            </div>

            <InsightsCard
              activeTrendValue={activeTrendValue}
              subscriptionCanceled={data.subscriptionCanceled}
            />
          </div>
        )}
      </div>

      <DashboardFooter />
    </div>
  );
}
